import { useCallback } from "react"
import { graphql } from "relay-runtime"
import { useCustomMutation } from "@/hooks/useCustomMutation"
import { GraphqlErrors } from "@/lib/utils"
import {
  UpdateAppointmentInput,
  useUpdateAppointmentMutation as updateAppointmentMutation,
  useUpdateAppointmentMutation$data,
} from "@/modules/patients/services/__generated__/useUpdateAppointmentMutation.graphql"

const mutation = graphql`
  mutation useUpdateAppointmentMutation($input: UpdateAppointmentInput!) {
    updateAppointment(input: $input) {
      errors {
        ... on Error {
          __typename
          message
        }
      }
      appointment {
        id
        ...PatientAppointmentFragment @relay(mask: false)
        initialBookedDateTime
        initialAssignedToUser {
          id
          firstName
          lastName
        }
        rescheduledBy
        rescheduledAt
        rescheduledByUser {
          id
          firstName
          lastName
        }
      }
    }
  }
`

export function useUpdateAppointmentMutation() {
  const [commit, isFlight] = useCustomMutation<updateAppointmentMutation>(mutation)

  const handler = useCallback(
    (
      input: UpdateAppointmentInput,
      existingFollowUpForProgressNoteId: string,
      onCompleted?: () => void,
      onMutationResultError?: (errors: GraphqlErrors) => void,
    ) => {
      return new Promise<NonNullable<useUpdateAppointmentMutation$data["updateAppointment"]["appointment"]>>(
        (resolve, reject) => {
          commit({
            variables: {
              input,
            },
            onCompleted(response) {
              if (response.updateAppointment.appointment) {
                onCompleted?.()
                resolve(response.updateAppointment.appointment)
              }
            },
            onError(error) {
              reject(error)
            },
            onMutationResultError(errors: GraphqlErrors) {
              onMutationResultError?.(errors)
              reject(errors)
            },
            updater: (store, response) => {
              const data = response?.updateAppointment

              if (data?.appointment && existingFollowUpForProgressNoteId !== input.followUpForProgressNoteId) {
                if (existingFollowUpForProgressNoteId) {
                  const progressNoteProxy = store.get(existingFollowUpForProgressNoteId)
                  progressNoteProxy?.setValue(null, "followUpAppointment")
                }

                if (input.followUpForProgressNoteId) {
                  const appointmentProxy = store.get(data.appointment.id)
                  const progressNoteProxy = store.get(input.followUpForProgressNoteId)

                  if (appointmentProxy) {
                    progressNoteProxy?.setLinkedRecord(appointmentProxy, "followUpAppointment")
                  }
                }
              }
            },
          })
        },
      )
    },
    [commit],
  )

  return [handler, isFlight] as const
}
